// v220 admin network page — air-gapped-by-default control plane
// (item 24 of PRODUCT-ROADMAP.md, "Air-Gapped-Mode als Default-
// Installation"; item 29 in the recommended delivery order).
//
// Single page, four sections:
//
//   1. Status banner — at-a-glance "Air-gapped" (red) /
//      "Egress selectively allowed" (amber, with sub-allow chips) /
//      "Egress unrestricted" (green, master toggle off).
//
//   2. Master toggle — "Allow service pings to public internet" with
//      a confirm-dialog the first time it's enabled. Disabled when
//      `air_gapped_mode = false` (the master kill-switch is off).
//
//   3. Per-channel sub-toggles — webhook delivery, OSV-feed sync,
//      OTel export, Sigstore Rekor lookups, generic telemetry.
//      Greyed out when the master toggle is off.
//
//   4. Recently blocked egress attempts — last N rows from
//      system_egress_audit so operators can see what would have
//      phoned home.

import { useEffect, useState } from 'react';
import {
  Lock,
  Unlock,
  AlertTriangle,
  Wifi,
  WifiOff,
  ShieldCheck,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  useNetworkSettings,
  setNetworkSettings,
  type NetworkSettings,
} from '../../api/network';

interface ToggleSpec {
  key: keyof Pick<
    NetworkSettings,
    | 'allow_webhooks'
    | 'allow_telemetry'
    | 'allow_sigstore_rekor'
    | 'allow_osv_feed'
    | 'allow_otel_export'
    | 'allow_scanner_db'
  >;
  label: string;
  description: string;
}

const subToggles: ToggleSpec[] = [
  {
    key: 'allow_webhooks',
    label: 'Webhooks',
    description:
      'Outbound HTTP POSTs from the webhook subscription queue and from notification channels (Teams, Slack-incoming).',
  },
  {
    key: 'allow_osv_feed',
    label: 'OSV vulnerability feed',
    description:
      'Hourly sync to api.osv.dev for fresh CVE / GHSA data. When denied, advisories come exclusively from operator-uploaded trust bundles.',
  },
  {
    key: 'allow_otel_export',
    label: 'OpenTelemetry export',
    description:
      'OTLP/HTTP trace export to the configured OTEL_EXPORTER_OTLP_ENDPOINT. Toggling requires a server restart to take effect on Init.',
  },
  {
    key: 'allow_sigstore_rekor',
    label: 'Sigstore Rekor lookups',
    description:
      'Online Rekor inclusion-proof lookups for cosign bundles. With this denied, sigstore verifies the embedded Rekor entry only.',
  },
  {
    key: 'allow_telemetry',
    label: 'General telemetry / phone-home',
    description:
      'Reserved for future health-ping / version-check call sites. No-op today; flip-on once an operator enables a specific telemetry feature.',
  },
  {
    key: 'allow_scanner_db',
    label: 'grype/trivy vulnerability-DB refresh',
    description:
      'Periodic download of fresh CVE data for the Tier-3 content scanners (Anchore grype-db listing, Aqua trivy-db on ghcr.io). When denied, scanners keep matching against whatever DB snapshot the scanner image last had.',
  },
];

function deriveStatus(s: NetworkSettings | undefined): {
  tone: 'success' | 'warning' | 'danger';
  label: string;
  Icon: typeof Lock;
} {
  if (!s) return { tone: 'warning', label: 'Loading…', Icon: WifiOff };
  if (!s.air_gapped_mode) {
    return { tone: 'success', label: 'Egress unrestricted', Icon: Wifi };
  }
  if (!s.allow_service_pings) {
    return { tone: 'danger', label: 'Air-gapped', Icon: Lock };
  }
  return {
    tone: 'warning',
    label: 'Egress selectively allowed',
    Icon: Unlock,
  };
}

export function AdminNetwork() {
  const { data, mutate, isLoading } = useNetworkSettings();
  const [draft, setDraft] = useState<NetworkSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Sync the draft with the server settings whenever they reload.
  useEffect(() => {
    if (data?.settings) setDraft(data.settings);
  }, [data?.settings]);

  const status = deriveStatus(data?.settings);
  const dirty =
    draft && data?.settings
      ? JSON.stringify(draft) !== JSON.stringify(data.settings)
      : false;

  const setField = <K extends keyof NetworkSettings>(k: K, v: NetworkSettings[K]) => {
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  // Two-step gate on enabling the master toggle: surface a confirm
  // dialog the first time the operator flips it ON. Subsequent
  // saves of the same state pass through silently.
  const onToggleMaster = (next: boolean) => {
    if (next && !data?.settings.allow_service_pings) {
      setConfirmOpen(true);
      return;
    }
    setField('allow_service_pings', next);
  };

  const confirmEnable = () => {
    setField('allow_service_pings', true);
    setConfirmOpen(false);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    try {
      await setNetworkSettings({
        air_gapped_mode: draft.air_gapped_mode,
        allow_service_pings: draft.allow_service_pings,
        allow_webhooks: draft.allow_webhooks,
        allow_telemetry: draft.allow_telemetry,
        allow_sigstore_rekor: draft.allow_sigstore_rekor,
        allow_osv_feed: draft.allow_osv_feed,
        allow_otel_export: draft.allow_otel_export,
        allow_scanner_db: draft.allow_scanner_db,
      });
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const recent = data?.recent_blocked ?? [];

  return (
    <AdminShell
      title="Network egress"
      description="Defense-in-depth default. Outbound calls to the public internet (webhooks, OSV-feed sync, OpenTelemetry export, Sigstore Rekor lookups) are denied unless explicitly opted in. Existing deployments preserve their pre-air-gapped behaviour via the install-time migration heuristic."
    >
      <Card>
        <CardHeader
          title="Status"
          description="Cached cluster-wide; new toggle takes effect within five seconds."
          actions={
            isLoading ? (
              <Badge tone="neutral">Loading…</Badge>
            ) : (
              <Badge tone={status.tone}>
                <status.Icon className="h-3 w-3" />
                {status.label}
              </Badge>
            )
          }
        />
        <CardBody>
          {data?.settings && (
            <div className="space-y-2 text-sm">
              <div className="text-fg-muted">
                Air-gapped mode is{' '}
                <span className="font-medium text-fg">
                  {data.settings.air_gapped_mode ? 'enabled' : 'disabled'}
                </span>
                {'. '}Master service-ping switch is{' '}
                <span className="font-medium text-fg">
                  {data.settings.allow_service_pings ? 'enabled' : 'disabled'}
                </span>
                {'.'}
              </div>
              {data.settings.updated_by && (
                <div className="text-fg-subtle">
                  Last changed by {data.settings.updated_by} on{' '}
                  {new Date(data.settings.updated_at).toLocaleString()}.
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {draft && (
        <Card>
          <CardHeader
            title="Master switches"
            description="Air-gapped mode is the umbrella kill-switch. Master service-ping consent is the operator opt-in that makes the per-channel toggles below take effect. Both must be ON for any egress; turning OFF either blocks all channels."
          />
          <CardBody className="space-y-5">
            <ToggleRow
              checked={draft.air_gapped_mode}
              onChange={(v) => setField('air_gapped_mode', v)}
              label="Air-gapped mode"
              description="When ON, outbound calls go through the egress gate. When OFF, every channel is allowed unconditionally — set this only when reverting a deployment to the legacy 'no gate' behaviour."
            />
            <ToggleRow
              checked={draft.allow_service_pings}
              onChange={onToggleMaster}
              disabled={!draft.air_gapped_mode}
              label="Allow service pings to public internet"
              description="Master consent. With this OFF, every per-channel toggle below is ignored and all egress is denied. Confirms once on enable."
              confirmIcon={<AlertTriangle className="h-4 w-4 text-warning shrink-0" />}
            />
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card>
          <CardHeader
            title="Per-channel allowances"
            description="Each channel is independent. A channel only takes effect when both master switches above are ON."
          />
          <CardBody className="space-y-5">
            {subToggles.map((t) => (
              <ToggleRow
                key={t.key}
                checked={draft[t.key]}
                onChange={(v) => setField(t.key, v)}
                disabled={!draft.air_gapped_mode || !draft.allow_service_pings}
                label={t.label}
                description={t.description}
              />
            ))}
          </CardBody>
          <CardFooter className="flex justify-end gap-2">
            {dirty && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => setDraft(data?.settings ?? null)}
              >
                Discard changes
              </Button>
            )}
            <Button variant="primary" disabled={busy || !dirty} onClick={save}>
              <ShieldCheck className="h-4 w-4" />
              Save settings
            </Button>
          </CardFooter>
        </Card>
      )}

      {confirmOpen && (
        <Card className="border-warning/30 bg-warning/5">
          <CardHeader
            title="Open outbound egress?"
            description="This unblocks the per-channel toggles below. Individual channels still need to be enabled before any outbound call is made."
            actions={<AlertTriangle className="h-5 w-5 text-warning" />}
          />
          <CardFooter className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmEnable}>
              Enable master switch
            </Button>
          </CardFooter>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Recently blocked egress attempts"
          description={`Most recent ${recent.length} call${recent.length === 1 ? '' : 's'} the gate denied. Useful for working out which sub-toggles you need to enable for your specific stack.`}
        />
        <CardBody>
          {recent.length === 0 ? (
            <p className="text-fg-muted text-sm">
              Nothing has been blocked yet — either nothing in the deployment is trying to phone home, or the gate is wide open.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">Kind</th>
                    <th className="py-2 pr-4 font-medium">Source</th>
                    <th className="py-2 pr-4 font-medium">Target URL</th>
                    <th className="py-2 pr-4 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((row) => (
                    <tr key={row.id} className="border-b border-border/50">
                      <td className="py-2 pr-4 text-fg-muted whitespace-nowrap">
                        {new Date(row.blocked_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge tone="warning">{row.kind}</Badge>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-fg">
                        {row.source}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-fg-muted break-all max-w-[24rem]">
                        {row.target_url}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-fg-subtle">
                        {row.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {err && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{err}</p>
          </CardBody>
        </Card>
      )}
    </AdminShell>
  );
}

interface ToggleRowProps {
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
  readonly disabled?: boolean;
  readonly label: string;
  readonly description: string;
  readonly confirmIcon?: React.ReactNode;
}

function ToggleRow({
  checked,
  onChange,
  disabled,
  label,
  description,
  confirmIcon,
}: ToggleRowProps) {
  return (
    <label
      className={`flex items-start gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 rounded border-border text-accent focus:ring-accent disabled:cursor-not-allowed"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-fg flex items-center gap-2">
          {label}
          {confirmIcon}
        </div>
        <p className="text-xs text-fg-muted mt-0.5 leading-relaxed">{description}</p>
      </div>
    </label>
  );
}
